/**
 * DocumentHubModal — Unified Document Import Hub (RetailCLIQ ERP)
 *
 * Flow:
 *   Step 1. Module Picker   → pick Financial / Purchase / Sales / Inventory
 *   Step 2. Document Type   → filtered by module
 *   Step 3. Import Method   → CSV/Excel OR AI/PDF
 *   Step 4. Upload          → attach file (stage=1 inside stepper)
 *   Step 5. Extract         → OCR / parse spreadsheet (stage=2)
 *   Step 6. Preview         → side-by-side doc + extracted values for cross-check (stage=3)
 *   Step 7. Review & Map    → StagingDetail: supplier/customer/GL matching
 *   Step 8. Confirm & Post  → commit to real ERP table
 *
 * "Active" doc types post REAL ERP transactions (JV, PO, GRN, Invoices, KYC)
 * via the same stored procedures the manual screens use.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  X, Upload, FileText, Loader2, CheckCircle2, Inbox, PlusCircle, Sparkles,
  ArrowLeft, Landmark, ShoppingCart, Receipt, Boxes, FileSpreadsheet, ScanLine,
  Eye, ChevronRight,
} from 'lucide-react';
import {API_BASE, getToken} from '../../lib/api';
const API = API_BASE;
import StagingInbox from './StagingInbox';
import StagingDetail from './StagingDetail';
import GlobalImportModal from '../import/GlobalImportModal';

const AI_STAGES  = ['Creating digitization job…', 'Uploading document for OCR…', 'Extracting fields…', 'Saving to staging…'];
const CSV_STAGES = ['Reading spreadsheet…', 'Validating rows…', 'Saving to staging…'];

const MODULES = [
  { id: 'financial', label: 'Financial', icon: Landmark,     desc: 'GL & Journal Vouchers, Opening Balances, Banking, KYC' },
  { id: 'purchase',  label: 'Purchase',  icon: ShoppingCart, desc: 'Purchase Orders, GRN, Purchase Invoices' },
  { id: 'sales',     label: 'Sales',     icon: Receipt,      desc: 'Sales Invoices & Returns' },
  { id: 'inventory', label: 'Inventory', icon: Boxes,        desc: 'Stock Transfers & Adjustments' },
];

const money = v => (v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function DocumentHubModal({ isOpen, onClose, theme }) {
  const isDark   = theme === 'dark';
  const [categories, setCategories] = useState([]);
  const [topTab, setTopTab]         = useState('new');
  const [pendingCount, setPendingCount] = useState(0);
  const [module, setModule]         = useState(null);
  const [category, setCategory]     = useState(null);
  const [method, setMethod]         = useState(null);
  const [stage, setStage]           = useState(1);          // 1=upload  2=extracting  3=preview
  const [file, setFile]             = useState(null);
  const [analyzing, setAnalyzing]   = useState(false);
  const [analyzeIdx, setAnalyzeIdx] = useState(0);
  const [detailId, setDetailId]     = useState(null);
  const [error, setError]           = useState('');
  const [previewData, setPreviewData] = useState(null);     // { stagingId, extracted, fileUrl }
  const fileUrlRef = useRef(null);

  const refreshPendingCount = useCallback(() => {
    fetch(`${API}/api/document-hub/staging?status=Pending`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.json()).then(d => setPendingCount((d.data || []).length)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setTopTab('new'); setModule(null); setCategory(null); setMethod(null);
    setStage(1); setFile(null); setDetailId(null); setError(''); setPreviewData(null);
    fetch(`${API}/api/document-hub/categories`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.json()).then(d => { if (d.success) setCategories(d.categories); }).catch(() => {});
    refreshPendingCount();
  }, [isOpen, refreshPendingCount]);

  // Cleanup blob URL on unmount / re-use
  useEffect(() => {
    return () => { if (fileUrlRef.current) URL.revokeObjectURL(fileUrlRef.current); };
  }, []);

  useEffect(() => {
    if (!analyzing) { setAnalyzeIdx(0); return; }
    const stages = method === 'csv' ? CSV_STAGES : AI_STAGES;
    const timers = stages.slice(1).map((_, i) =>
      setTimeout(() => setAnalyzeIdx(i + 1), method === 'csv' ? (i + 1) * 500 : [2500, 12000, 30000][i])
    );
    return () => timers.forEach(clearTimeout);
  }, [analyzing, method]);

  const handleAnalyze = useCallback(async () => {
    if (!file || !category) return;
    setAnalyzing(true); setError('');
    try {
      // Build local URL for preview BEFORE upload (stays valid in current page session)
      if (fileUrlRef.current) URL.revokeObjectURL(fileUrlRef.current);
      fileUrlRef.current = URL.createObjectURL(file);

      const form = new FormData();
      form.append('file', file);
      form.append('category', category.id);
      const res  = await fetch(`${API}/api/document-hub/stage`, {
        method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: form,
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Extraction failed');

      // Go to preview instead of immediately opening staging inbox
      setPreviewData({
        stagingId:  data.data.id,
        extracted:  data.data.extracted || {},
        fileUrl:    fileUrlRef.current,
        mimeType:   file.type,
        fileName:   file.name,
      });
      setStage(3);
      refreshPendingCount();
    } catch (err) { setError(err.message); }
    setAnalyzing(false);
  }, [file, category, refreshPendingCount]);

  const goToDetail = () => {
    setDetailId(previewData.stagingId);
    setPreviewData(null);
  };

  const discardAndRestart = async () => {
    // Delete the staging record and start over
    if (previewData?.stagingId) {
      try {
        await fetch(`${API}/api/document-hub/staging/${previewData.stagingId}`, {
          method: 'DELETE', headers: { Authorization: `Bearer ${getToken()}` },
        });
      } catch (_) {}
    }
    setPreviewData(null);
    setStage(1); setFile(null); setError('');
    refreshPendingCount();
  };

  if (!isOpen) return null;

  const bg     = isDark ? '#0a0a0a' : '#f8fafc';
  const bgCard = isDark ? '#141414' : '#ffffff';
  const border = isDark ? '#262626' : '#e2e8f0';
  const text   = isDark ? '#e2e8f0' : '#1e293b';
  const textMut = isDark ? '#6b7280' : '#94a3b8';
  const common  = { isDark, border, bgCard, text, textMut };

  const categoriesInModule = categories.filter(c => c.module === module?.id);
  const subtitle = category
    ? (method ? `${category.label} · ${method === 'csv' ? 'CSV/Excel' : 'AI/PDF'} import` : category.label)
    : module ? `${module.label} — pick a document type`
    : 'Module Picker — Financial, Purchase, Sales & Inventory imports that post real transactions';

  return (
    <div data-testid="document-hub-modal" style={{ position: 'fixed', inset: 0, zIndex: 10000, background: bg, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: `1px solid ${border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Sparkles size={18} color="#8b5cf6" />
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: text }}>Document Hub</div>
            <div style={{ fontSize: 11, color: textMut }}>{subtitle}</div>
          </div>
        </div>
        <button data-testid="doc-hub-close-btn" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: textMut }}><X size={20} /></button>
      </div>

      {detailId ? (
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 32px', maxWidth: 1100, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
          <StagingDetail id={detailId} {...common}
            onBack={() => { setDetailId(null); setTopTab('pending'); refreshPendingCount(); }} />
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 6, padding: '10px 24px 0', borderBottom: `1px solid ${border}` }}>
            {[
              { id: 'new',     label: 'New Import',      icon: PlusCircle },
              { id: 'pending', label: 'Pending Imports',  icon: Inbox, badge: pendingCount },
            ].map(t => {
              const TIcon  = t.icon;
              const active = topTab === t.id;
              return (
                <button key={t.id} data-testid={`doc-hub-tab-${t.id}`} onClick={() => setTopTab(t.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px',
                    borderRadius: '10px 10px 0 0', border: 'none',
                    borderBottom: active ? '2px solid #8b5cf6' : '2px solid transparent',
                    background: 'transparent', color: active ? '#8b5cf6' : textMut,
                    fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  <TIcon size={13} />{t.label}
                  {!!t.badge && (
                    <span style={{ fontSize: 9, fontWeight: 800, padding: '1px 6px', borderRadius: 10,
                      background: active ? '#8b5cf6' : `${textMut}30`, color: active ? '#fff' : textMut }}>
                      {t.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {topTab === 'pending' ? (
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
              <StagingInbox {...common} onOpen={(id) => { setDetailId(id); }} />
            </div>
          ) : !module ? (
            <ModulePicker {...common} onSelect={setModule} />
          ) : !category ? (
            <CategoryGrid module={module} items={categoriesInModule} {...common}
              onBack={() => setModule(null)} onSelect={c => { if (c.status === 'active') setCategory(c); }} />
          ) : category.legacy ? (
            <GlobalImportModal isOpen onClose={() => setCategory(null)} theme={theme} preSelectedType={category.id} />
          ) : !method ? (
            <MethodPicker category={category} {...{ border, bgCard, text, textMut }}
              onBack={() => setCategory(null)} onSelect={setMethod} />
          ) : (
            <>
              <Stepper stage={stage} border={border} text={text} textMut={textMut} />
              <div style={{ flex: 1, overflowY: 'auto', padding: '20px 32px',
                maxWidth: stage === 3 ? 1200 : 700, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
                {error && (
                  <div data-testid="doc-hub-error" style={{ padding: '10px 14px', borderRadius: 8,
                    background: '#ef444420', border: '1px solid #ef4444', color: '#ef4444',
                    fontSize: 12, marginBottom: 16 }}>
                    {error}
                  </div>
                )}

                {stage === 1 && (
                  <AttachStage file={file} setFile={setFile} method={method}
                    {...{ border, bgCard, text, textMut }}
                    onNext={() => setStage(2)} onCancel={() => setMethod(null)} />
                )}

                {stage === 2 && (
                  <AnalyseStage analyzing={analyzing} analyzeIdx={analyzeIdx} method={method}
                    {...{ border, bgCard, text, textMut }}
                    onAnalyze={handleAnalyze} onBack={() => setStage(1)} />
                )}

                {stage === 3 && previewData && (
                  <PreviewStage
                    previewData={previewData}
                    category={category}
                    {...common}
                    onContinue={goToDetail}
                    onDiscard={discardAndRestart}
                  />
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

/* ─── Module Picker ─── */
function ModulePicker({ isDark, border, bgCard, text, textMut, onSelect }) {
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '32px 24px' }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.08em', color: textMut, marginBottom: 16, textTransform: 'uppercase', textAlign: 'center' }}>
        Step 1 — Choose a Module
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 16, maxWidth: 980, margin: '0 auto' }}>
        {MODULES.map(m => {
          const MIcon = m.icon;
          return (
            <div key={m.id} data-testid={`doc-hub-module-${m.id}`} onClick={() => onSelect(m)}
              style={{ padding: 24, borderRadius: 16, border: `1px solid ${border}`, background: bgCard, cursor: 'pointer', transition: 'transform .12s, border-color .12s' }}
              onMouseEnter={e => { if (!e.currentTarget) return;  e.currentTarget.style.borderColor = '#8b5cf6'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
              onMouseLeave={e => { if (!e.currentTarget) return;  e.currentTarget.style.borderColor = border; e.currentTarget.style.transform = 'none'; }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: '#8b5cf620', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                <MIcon size={22} color="#8b5cf6" />
              </div>
              <div style={{ fontSize: 15, fontWeight: 800, color: text }}>{m.label}</div>
              <div style={{ fontSize: 11, color: textMut, marginTop: 6, lineHeight: 1.5 }}>{m.desc}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Category Grid ─── */
function CategoryGrid({ module, items, isDark, border, bgCard, text, textMut, onSelect, onBack }) {
  const ModIcon = module.icon;
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
      <button data-testid="doc-hub-back-to-modules-btn" onClick={onBack}
        style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: textMut, fontSize: 12, fontWeight: 600, marginBottom: 16 }}>
        <ArrowLeft size={13} /> All Modules
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <ModIcon size={16} color="#8b5cf6" />
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.08em', color: textMut, textTransform: 'uppercase' }}>
          Step 2 — {module.label} Document Type
        </div>
      </div>
      {items.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: textMut, fontSize: 12 }}>
          More {module.label.toLowerCase()} document types are coming soon.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
          {items.map(c => (
            <div key={c.id} data-testid={`doc-hub-category-${c.id}`} onClick={() => onSelect(c)}
              style={{ padding: 16, borderRadius: 12, border: `1px solid ${border}`, background: bgCard,
                cursor: c.status === 'active' ? 'pointer' : 'default', opacity: c.status === 'active' ? 1 : 0.5,
                transition: 'transform .12s, border-color .12s' }}
              onMouseEnter={e => { if (!e.currentTarget) return; if (c.status === 'active') { e.currentTarget.style.borderColor = '#8b5cf6'; e.currentTarget.style.transform = 'translateY(-2px)'; } }}
              onMouseLeave={e => { if (!e.currentTarget) return;  e.currentTarget.style.borderColor = border; e.currentTarget.style.transform = 'none'; }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <FileText size={18} color={c.status === 'active' ? '#8b5cf6' : textMut} />
                {c.status === 'coming_soon' && (
                  <span style={{ fontSize: 8, fontWeight: 800, padding: '2px 6px', borderRadius: 4, background: `${textMut}20`, color: textMut }}>SOON</span>
                )}
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: text, marginTop: 10 }}>{c.label}</div>
              <div style={{ fontSize: 10, color: textMut, marginTop: 4 }}>{c.target}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Method Picker ─── */
function MethodPicker({ category, border, bgCard, text, textMut, onSelect, onBack }) {
  const options = [
    category.csv && { id: 'csv', icon: FileSpreadsheet, label: 'CSV / Excel', desc: 'Upload a spreadsheet with the required columns — parsed instantly, no OCR.' },
    category.ai  && { id: 'ai',  icon: ScanLine,        label: 'AI / PDF Extraction', desc: 'Upload a scanned PDF or photo — Sarvam AI OCR extracts the fields for you.' },
  ].filter(Boolean);
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
      <button data-testid="doc-hub-back-to-categories-btn" onClick={onBack}
        style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: textMut, fontSize: 12, fontWeight: 600, marginBottom: 16 }}>
        <ArrowLeft size={13} /> {category.label}
      </button>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.08em', color: textMut, marginBottom: 16, textTransform: 'uppercase', textAlign: 'center' }}>
        Step 3 — Import Method
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${options.length}, 1fr)`, gap: 16, maxWidth: 640, margin: '0 auto' }}>
        {options.map(o => {
          const OIcon = o.icon;
          return (
            <div key={o.id} data-testid={`doc-hub-method-${o.id}`} onClick={() => onSelect(o.id)}
              style={{ padding: 22, borderRadius: 14, border: `1px solid ${border}`, background: bgCard, cursor: 'pointer', transition: 'transform .12s, border-color .12s' }}
              onMouseEnter={e => { if (!e.currentTarget) return;  e.currentTarget.style.borderColor = '#8b5cf6'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { if (!e.currentTarget) return;  e.currentTarget.style.borderColor = border; e.currentTarget.style.transform = 'none'; }}>
              <OIcon size={22} color="#8b5cf6" style={{ marginBottom: 10 }} />
              <div style={{ fontSize: 13, fontWeight: 800, color: text }}>{o.label}</div>
              <div style={{ fontSize: 11, color: textMut, marginTop: 6, lineHeight: 1.5 }}>{o.desc}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Stepper (3 stages: Upload / Extract / Preview) ─── */
function Stepper({ stage, border, text, textMut }) {
  const labels = ['Upload', 'Extract', 'Preview'];
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px 24px', borderBottom: `1px solid ${border}` }}>
      {labels.map((label, i) => {
        const n       = i + 1;
        const isDone  = stage > n;
        const isActive = stage === n;
        return (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div data-testid={`doc-hub-stage-${n}`} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 8px', borderRadius: 6, background: isActive ? '#8b5cf620' : 'transparent' }}>
              {isDone
                ? <CheckCircle2 size={12} color="#22c55e" />
                : <div style={{ width: 12, height: 12, borderRadius: '50%', border: `1.5px solid ${isActive ? '#8b5cf6' : textMut}` }} />
              }
              <span style={{ fontSize: 11, fontWeight: isActive ? 700 : 500, color: isActive ? '#8b5cf6' : isDone ? text : textMut }}>
                {n}. {label}
              </span>
            </div>
            {i < labels.length - 1 && <div style={{ width: 24, height: 1, background: border }} />}
          </div>
        );
      })}
    </div>
  );
}

/* ─── Attach Stage ─── */
function AttachStage({ file, setFile, method, border, bgCard, text, textMut, onNext, onCancel }) {
  const accept = method === 'csv' ? '.csv,.xlsx,.xls' : '.pdf,.jpg,.jpeg,.png,.webp';
  const hint   = method === 'csv' ? 'CSV or Excel (.xlsx/.xls) · up to 20MB' : 'PDF, JPG or PNG · up to 20MB';
  return (
    <div>
      <div data-testid="doc-hub-dropzone"
        style={{ border: `2px dashed ${border}`, borderRadius: 14, padding: 48, textAlign: 'center', background: bgCard, cursor: 'pointer' }}
        onClick={() => document.getElementById('doc-hub-file-input').click()}>
        <Upload size={32} color={textMut} style={{ marginBottom: 12 }} />
        <div style={{ fontSize: 13, fontWeight: 700, color: text }}>{file ? file.name : 'Click to attach a document'}</div>
        <div style={{ fontSize: 11, color: textMut, marginTop: 4 }}>{hint}</div>
        <input id="doc-hub-file-input" type="file" accept={accept} style={{ display: 'none' }}
          onChange={e => setFile(e.target.files?.[0] || null)} />
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
        <button data-testid="doc-hub-attach-cancel-btn" onClick={onCancel}
          style={{ padding: '10px 20px', borderRadius: 10, border: `1px solid ${border}`, background: 'transparent', color: text, fontSize: 12, cursor: 'pointer' }}>
          Change method
        </button>
        <button data-testid="doc-hub-attach-next-btn" disabled={!file} onClick={onNext}
          style={{ flex: 1, padding: '11px', borderRadius: 10, border: 'none', background: file ? '#8b5cf6' : `${textMut}40`, color: '#fff', fontSize: 13, fontWeight: 700, cursor: file ? 'pointer' : 'not-allowed' }}>
          Continue to Extract
        </button>
      </div>
    </div>
  );
}

/* ─── Analyse Stage ─── */
function AnalyseStage({ analyzing, analyzeIdx, method, border, bgCard, text, textMut, onAnalyze, onBack }) {
  const stages    = method === 'csv' ? CSV_STAGES : AI_STAGES;
  const readyLabel = method === 'csv'
    ? 'Ready to read your spreadsheet and extract the data for your review.'
    : 'Ready to run Sarvam AI OCR on your document and extract the data for your review.';
  return (
    <div>
      {!analyzing ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <div style={{ fontSize: 13, color: textMut, marginBottom: 20 }}>{readyLabel}</div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button onClick={onBack} style={{ padding: '11px 20px', borderRadius: 10, border: `1px solid ${border}`, background: 'transparent', color: text, fontSize: 12, cursor: 'pointer' }}>Back</button>
            <button data-testid="doc-hub-analyze-btn" onClick={onAnalyze}
              style={{ padding: '11px 28px', borderRadius: 10, border: 'none', background: '#8b5cf6', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              Start Extraction
            </button>
          </div>
        </div>
      ) : (
        <div data-testid="doc-hub-analyze-progress" style={{ padding: '12px 14px', borderRadius: 10, border: `1px solid ${border}`, background: bgCard }}>
          {stages.map((label, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '6px 0', opacity: i <= analyzeIdx ? 1 : 0.4 }}>
              {i < analyzeIdx
                ? <CheckCircle2 size={14} color="#22c55e" />
                : i === analyzeIdx
                  ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite', color: '#8b5cf6' }} />
                  : <div style={{ width: 14, height: 14, borderRadius: '50%', border: `1.5px solid ${border}` }} />
              }
              <span style={{ fontSize: 12, color: i <= analyzeIdx ? text : textMut, fontWeight: i === analyzeIdx ? 700 : 400 }}>{label}</span>
            </div>
          ))}
        </div>
      )}
      <style>{`@keyframes spin { from{transform:rotate(0)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
}

/* ─── Preview Stage — split-screen: left=doc, right=extracted fields ─── */
function PreviewStage({ previewData, category, isDark, border, bgCard, text, textMut, onContinue, onDiscard }) {
  const { fileUrl, mimeType, fileName, extracted } = previewData;
  const isImage = /^image\//.test(mimeType || '');
  const isPdf   = mimeType === 'application/pdf';

  return (
    <div>
      {/* Instruction banner */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, background: '#8b5cf610', border: '1px solid #8b5cf630', marginBottom: 16 }}>
        <Eye size={14} color="#8b5cf6" />
        <span style={{ fontSize: 12, color: isDark ? '#c4b5fd' : '#6d28d9', fontWeight: 600 }}>
          Step 4 — Preview &amp; Cross-check &nbsp;·&nbsp; Verify the extracted values match your original document, then continue to map &amp; post.
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>
        {/* LEFT — Original document */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: textMut, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.05em' }}>
            Original Document — {fileName}
          </div>
          <div style={{ border: `1px solid ${border}`, borderRadius: 12, overflow: 'hidden', minHeight: 520, maxHeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', background: isDark ? '#0f0f0f' : '#f1f5f9' }}>
            {isImage && (
              <img data-testid="doc-hub-preview-img" src={fileUrl} alt={fileName}
                style={{ maxWidth: '100%', maxHeight: 600, objectFit: 'contain' }} />
            )}
            {isPdf && (
              <iframe data-testid="doc-hub-preview-pdf" title="Document Preview" src={fileUrl}
                style={{ width: '100%', height: 580, border: 'none' }} />
            )}
            {!isImage && !isPdf && (
              <div style={{ textAlign: 'center', padding: 32 }}>
                <FileText size={40} color={textMut} style={{ marginBottom: 12 }} />
                <div style={{ fontSize: 12, color: textMut }}>Spreadsheet uploaded</div>
                <div style={{ fontSize: 11, color: textMut, marginTop: 4 }}>{fileName}</div>
                <div style={{ fontSize: 10, color: textMut, marginTop: 8, background: isDark ? '#1a1a1a' : '#f8fafc', padding: '6px 12px', borderRadius: 6, display: 'inline-block' }}>
                  CSV/Excel files are parsed directly — preview not available. Verify the extracted rows on the right.
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — Extracted values */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: textMut, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.05em' }}>
            Extracted Values — {(category?.label || '').toUpperCase()}
          </div>
          <div style={{ border: `1px solid ${border}`, borderRadius: 12, overflow: 'hidden', maxHeight: 600, overflowY: 'auto' }}>
            <ExtractedSummary extracted={extracted} docType={category?.id} isDark={isDark} border={border} bgCard={bgCard} text={text} textMut={textMut} />
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
        <button data-testid="doc-hub-preview-discard-btn" onClick={onDiscard}
          style={{ padding: '10px 20px', borderRadius: 10, border: `1px solid ${border}`, background: 'transparent', color: '#ef4444', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          Discard &amp; Start Over
        </button>
        <button data-testid="doc-hub-preview-continue-btn" onClick={onContinue}
          style={{ flex: 1, padding: '12px', borderRadius: 10, border: 'none', background: '#8b5cf6', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <span>Looks Correct — Continue to Review &amp; Map</span>
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

/* ─── Extracted fields summary (right panel of Preview) ─── */
function ExtractedSummary({ extracted, docType, isDark, border, bgCard, text, textMut }) {
  const isJournal  = docType === 'journal_voucher';
  const isPO       = docType === 'purchase_order';
  const isGRN      = docType === 'grn';
  const isInvoice  = ['purchase_invoice', 'sales_invoice', 'sales_return'].includes(docType);
  const isKyc      = ['pan_card', 'gst_certificate'].includes(docType);

  const field = (label, value, isHighlight = false) => (
    <div style={{ display: 'flex', padding: '9px 14px', borderTop: `1px solid ${border}`, gap: 10 }}>
      <div style={{ fontSize: 11, color: textMut, width: 130, flexShrink: 0 }}>{label}</div>
      <div style={{
        fontSize: 12, fontWeight: 600,
        color: value ? (isHighlight ? '#22c55e' : text) : '#f59e0b',
        fontFamily: typeof value === 'number' ? 'monospace' : 'inherit',
        flex: 1,
      }}>
        {value !== undefined && value !== null && value !== ''
          ? (typeof value === 'number' ? value.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : String(value))
          : <span style={{ color: '#f59e0b', fontStyle: 'italic' }}>Not detected</span>
        }
      </div>
    </div>
  );

  const headerBg = isDark ? '#1a1a1a' : '#f8fafc';

  const SectionHeader = ({ label, icon: Icon, count }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: headerBg, borderTop: `1px solid ${border}` }}>
      {Icon && <Icon size={13} color="#8b5cf6" />}
      <span style={{ fontSize: 10, fontWeight: 800, color: textMut, textTransform: 'uppercase', letterSpacing: '.06em' }}>{label}</span>
      {count !== undefined && <span style={{ fontSize: 9, padding: '1px 7px', borderRadius: 10, background: '#8b5cf620', color: '#8b5cf6', fontWeight: 700, marginLeft: 'auto' }}>{count}</span>}
    </div>
  );

  const lineItems   = extracted.lineItems   || [];
  const taxLines    = extracted.taxLines    || [];
  const grandTotal  = extracted.grandTotal  || extracted.totalDr || 0;

  return (
    <div style={{ background: bgCard }}>
      {/* ── Document header fields ── */}
      <SectionHeader label="Document Header" icon={FileText} />
      {isKyc && <>
        {field('Name / Entity', extracted.name || extracted.legalName)}
        {field('PAN Number',    extracted.panNo,   true)}
        {field('GSTIN',         extracted.gstNo,   true)}
        {field('Address',       extracted.address)}
      </>}

      {(isPO || isGRN || isInvoice) && <>
        {field('Party Name',    extracted.partyName)}
        {field('Invoice / Doc No.', extracted.invoiceNo)}
        {field('Date',          extracted.invoiceDate)}
        {isGRN && field('PO Reference', extracted.poNo)}
        {field('Grand Total',   grandTotal > 0 ? grandTotal : undefined, grandTotal > 0)}
      </>}

      {isJournal && <>
        {field('Voucher No.',   extracted.voucherNo)}
        {field('Voucher Date',  extracted.voucherDate)}
        {field('Narration',     extracted.narration)}
        {field('Total Debit',   extracted.totalDr,  true)}
        {field('Total Credit',  extracted.totalCr,  true)}
      </>}

      {!isKyc && !isPO && !isGRN && !isInvoice && !isJournal && <>
        {Object.entries(extracted)
          .filter(([k]) => !['lineItems','taxLines'].includes(k))
          .map(([k, v]) => field(k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()), v))
        }
      </>}

      {/* ── Line items ── */}
      {lineItems.length > 0 && (
        <>
          <SectionHeader label={isJournal ? 'GL Entries' : 'Line Items'} count={lineItems.length} />
          <div style={{ maxHeight: 260, overflowY: 'auto' }}>
            {lineItems.slice(0, 15).map((li, i) => (
              <div key={i} style={{ padding: '8px 14px', borderTop: i > 0 ? `1px solid ${border}` : `1px solid ${border}`, fontSize: 11 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: text, fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {li.description || li.particular || li.ItemDesc || `Line ${i + 1}`}
                  </span>
                  {isJournal ? (
                    <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
                      {li.debit  > 0 && <span style={{ color: '#22c55e',  fontFamily: 'monospace', fontWeight: 700 }}>Dr {money(li.debit)}</span>}
                      {li.credit > 0 && <span style={{ color: '#3b82f6',  fontFamily: 'monospace', fontWeight: 700 }}>Cr {money(li.credit)}</span>}
                    </div>
                  ) : (
                    <span style={{ color: textMut, fontFamily: 'monospace', flexShrink: 0 }}>
                      {li.qty ? `${li.qty} × ` : ''}{li.amount ? money(li.amount) : '—'}
                    </span>
                  )}
                </div>
                {isJournal && li.glCode && (
                  <div style={{ fontSize: 10, color: textMut, marginTop: 2 }}>GL: {li.glCode}</div>
                )}
                {!isJournal && (li.hsn || li.gstPct) && (
                  <div style={{ fontSize: 10, color: textMut, marginTop: 2 }}>
                    {li.hsn ? `HSN: ${li.hsn}` : ''}{li.hsn && li.gstPct ? '  ·  ' : ''}{li.gstPct ? `GST: ${li.gstPct}%` : ''}
                  </div>
                )}
              </div>
            ))}
            {lineItems.length > 15 && (
              <div style={{ padding: '8px 14px', fontSize: 10, color: textMut, borderTop: `1px solid ${border}`, textAlign: 'center' }}>
                …and {lineItems.length - 15} more line items
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Tax lines ── */}
      {taxLines.length > 0 && (
        <>
          <SectionHeader label="Tax &amp; Charges" count={taxLines.length} />
          {taxLines.map((tl, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 14px', borderTop: `1px solid ${border}`, fontSize: 11 }}>
              <span style={{ color: text }}>{tl.description}</span>
              <span style={{ color: '#f59e0b', fontFamily: 'monospace', fontWeight: 700 }}>{money(tl.amount)}</span>
            </div>
          ))}
        </>
      )}

      {lineItems.length === 0 && taxLines.length === 0 && !isKyc && (
        <div style={{ padding: 20, fontSize: 11, color: textMut, textAlign: 'center' }}>
          No line items were extracted from this document.
        </div>
      )}
    </div>
  );
}
